import type { Locale } from "@porsche-customer/slfinrtl-context-types"
import type { Environment } from "@porsche-customer/slfinrtl-environment"
import type { NewCarOrderIdIdentifier } from "@slfinrtl/ui-bff-routes"
import type { AddOfferQueryParams } from "./buildAddOfferUrl"
import type { EditOfferQueryParams } from "./buildEditOfferUrl"

export interface ConfiguratorLoadConfigurationCommonParams {
	backActionUrl?: string
	canReportSales: boolean
	checkDate?: string
	ctaMode: "reportSale" | "updateSale" | "non-changeable"
	dealership: string
	environment: Environment | null
	jumpToSummarySection?: boolean
	locale: Locale
	offerId?: string
	priceDate?: string
}

export type ConfiguratorVehicleIdentifierParams =
	| { newCarOrderId?: never; porscheCode: string }
	| { newCarOrderId: NewCarOrderIdIdentifier; porscheCode?: never }

export type ConfiguratorLoadConfigurationParams = ConfiguratorLoadConfigurationCommonParams &
	ConfiguratorVehicleIdentifierParams

interface BuildConfiguratorLoadConfigurationUrlOptions {
	getOfferAddPath?: (query: AddOfferQueryParams) => string
	getOfferEditPath?: (offerId: string, query?: EditOfferQueryParams) => string
	origin: string
}

interface Action {
	label: string
	url: string
}

function getConfiguratorHost(environment: Environment | null): string {
	return environment === "prod"
		? "https://configurator.porsche.com"
		: environment === "preview"
			? "https://configurator-staging.porsche.com"
			: "https://configurator-dev.porsche.com"
}

export function buildConfiguratorLoadConfigurationUrl(
	params: ConfiguratorLoadConfigurationParams,
	options: BuildConfiguratorLoadConfigurationUrlOptions,
): string {
	const {
		backActionUrl,
		canReportSales,
		checkDate,
		ctaMode,
		dealership,
		environment,
		jumpToSummarySection = false,
		locale,
		offerId,
		porscheCode,
		priceDate,
	} = params
	const { getOfferAddPath, getOfferEditPath, origin } = options

	const isLoadedByMic = porscheCode === undefined
	const presentationMode = ctaMode !== "non-changeable" ? "model" : "readonly"

	const configurationPath = getConfigurationPath(presentationMode, params)

	const isOfferEditMode = !!offerId

	// We have to use a placeholder here for the porscheCode because it will be replaced with the actual
	// porscheCode in the returnUrl by the configurator.
	const porscheCodePlaceholder = "{porscheCode}" as const

	const offerAddReturnPath =
		porscheCode && getOfferAddPath ? getOfferAddPath({ porscheCode: porscheCodePlaceholder }) : undefined
	const offerAddReturnPathWithReportSale =
		porscheCode && getOfferAddPath
			? getOfferAddPath({ porscheCode: porscheCodePlaceholder, reportSale: true })
			: undefined

	const offerEditReturnPath =
		offerId && getOfferEditPath ? getOfferEditPath(offerId, { porscheCode: porscheCodePlaceholder }) : undefined
	const offerEditReturnPathWithReportSale =
		offerId && getOfferEditPath
			? getOfferEditPath(offerId, { porscheCode: porscheCodePlaceholder, reportSale: true })
			: undefined

	const toAbsoluteReturnUrl = (path: string | undefined) =>
		path ? new URL(`${dealership}/${locale}${path}`, origin).toString() : undefined

	const returnUrl = isOfferEditMode
		? toAbsoluteReturnUrl(offerEditReturnPath)
		: toAbsoluteReturnUrl(offerAddReturnPath)

	const returnUrlReportSale = isOfferEditMode
		? toAbsoluteReturnUrl(offerEditReturnPathWithReportSale)
		: toAbsoluteReturnUrl(offerAddReturnPathWithReportSale)

	// ctaMode --> for determining sale-button text & show/hide CTA-button behavior
	// only include return urls for "Report Sale" / "Update Sale" button if ctaMode is not "non-changeable"
	const shouldIncludeReturnUrls = ctaMode !== "non-changeable"

	const searchParams = new URLSearchParams()

	const actions: Action[] = []

	if (canReportSales && shouldIncludeReturnUrls && returnUrlReportSale !== undefined) {
		actions.push({
			label:
				ctaMode === "reportSale"
					? "dealer-cta_sale-save-configuration-button"
					: "dealer-cta_sale-update-configuration-button",
			url: returnUrlReportSale,
		})
	}

	if (shouldIncludeReturnUrls && returnUrl !== undefined) {
		actions.push({
			label: "dealer-cta_offer-save-configuration-button",
			url: returnUrl,
		})
	}

	const [primaryAction, secondaryAction] = actions

	if (primaryAction) {
		searchParams.set("primaryAction[url]", primaryAction.url)
		searchParams.set("primaryAction[label]", primaryAction.label)
	}

	if (secondaryAction) {
		searchParams.set("secondaryAction[url]", secondaryAction.url)
		searchParams.set("secondaryAction[label]", secondaryAction.label)
	}

	if (backActionUrl !== undefined) {
		searchParams.set("backAction[url]", backActionUrl)
		searchParams.set("backAction[label]", "actions_back")
	}

	if (checkDate && ctaMode === "updateSale") {
		searchParams.set("checkDate", checkDate)
	}

	if (priceDate && ctaMode === "updateSale") {
		searchParams.set("priceDate", priceDate)
	}

	if (ctaMode === "updateSale" && !isLoadedByMic) {
		searchParams.set("keepInvisibleOptions", "true")
	}

	const queryParamsUrlPart = `?${searchParams.toString()}`

	const jumpToSummarySectionUrlPart = jumpToSummarySection ? "#section-summary" : ""

	if (configurationPath === undefined) {
		return `${getConfiguratorHost(environment)}/dealer/${locale}/${dealership}${queryParamsUrlPart}`
	}

	return `${getConfiguratorHost(environment)}/dealer/${locale}/${dealership}/${configurationPath}${queryParamsUrlPart}${jumpToSummarySectionUrlPart}`
}

function getConfigurationPath(
	presentationMode: "model" | "readonly",
	params: ConfiguratorLoadConfigurationParams,
): string | undefined {
	if (params.porscheCode) {
		return `mode/${presentationMode}/load-configuration/porsche-code/${params.porscheCode}`
	}

	if (params.newCarOrderId) {
		return `mode/${presentationMode}/load-configuration/new-car-order-id/${params.newCarOrderId.newCarOrderId}`
	}
}
